import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, Clock, DollarSign, AlertCircle, Award, ChevronRight, Cloud } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

interface Lance {
  id: number;
  empresa: string;
  valor: number;
  data: string;
  status: string;
}

const PregaoLances: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [valorLance, setValorLance] = useState<string>('');
  const [lances, setLances] = useState<Lance[]>([
    { id: 2, empresa: 'Idbod', valor: 14000, data: '2023-05-10T10:15:00', status: 'aceito' },
    { id: 3, empresa: 'Stickler Digital', valor: 10500, data: '2023-05-10T10:05:00', status: 'aceito' },
    { id: 4, empresa: 'Idbod', valor: 7500, data: '2023-05-10T09:55:00', status: 'aceito' },
    { id: 5, empresa: 'Stickler Digital', valor: 5500, data: '2023-05-10T09:45:00', status: 'aceito' },
    { id: 6, empresa: 'Idbod', valor: 2500, data: '2023-05-10T09:35:00', status: 'aceito' }
  ]);
  const [tempoRestante, setTempoRestante] = useState<number>(1324);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);
  // Adicionar estado para o lote selecionado
  const [loteSelecionado, setLoteSelecionado] = useState<number | null>(null);

  const creditosDisponiveis = 1634488;
  const nome = 'Inserte L.';
  const pontos = 23.914;
  
  // Formatador de valores em reais
  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  // Formatador de grandes números
  const formatarNumeroGrande = (numero: number): string => {
    return numero.toLocaleString('pt-BR');
  };
  
  // Envia um lance
  const enviarLance = (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    
    // Validação
    if (!valorLance || isNaN(Number(valorLance)) || Number(valorLance) <= 0) {
      setErro('Por favor, informe um valor válido para o lance.');
      return;
    }
    
    const valor = Number(valorLance);
    const maiorLanceAtual = lances.length > 0 ? lances[0].valor : 0;
    
    if (valor <= maiorLanceAtual) {
      setErro(`Seu lance deve ser maior que o lance atual de ${formatarValor(maiorLanceAtual)}.`);
      return;
    }
    
    if (valor < maiorLanceAtual + 500) {
      setErro(`Seu lance deve superar o atual em pelo menos R$ 500,00.`);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulando envio para API
    setTimeout(() => {
      const novoLance: Lance = {
        id: Date.now(),
        empresa: 'Sua Empresa',
        valor: valor,
        data: new Date().toISOString(),
        status: 'aceito'
      };
      
      // Adiciona o novo lance e ordena por valor (maior para menor)
      setLances([novoLance, ...lances].sort((a, b) => b.valor - a.valor));
      setValorLance('');
      setIsSubmitting(false);
    }, 1000);
  };
  
  // Formatar tempo restante
  const formatarTempo = (horas: number): string => {
    const horasAbs = Math.abs(horas);
    return `-${horasAbs} horas`;
  };
  
  // Volta para o dashboard
  const voltarParaDashboard = () => {
    navigate('/pregao-dashboard');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <UserHeader />
      
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex items-center mb-4">
            <button 
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              onClick={voltarParaDashboard}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Lance em Pregão Eletrônico</h1>
              <p className="text-gray-600">Pregão de Créditos de Carbono</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex-grow p-6">
        <div className="container mx-auto">
          {/* Card Principal Estilo Dark */}
          <Card className="bg-[#0e7cb4] text-white mb-6 overflow-hidden">
            <CardContent className="p-0">
              {/* Número do lote selecionado */}
              <div className="p-4">
                <h2 className="text-lg font-bold text-white">
                  {loteSelecionado ? `Lote Nº LT-2025-0${loteSelecionado}` : 'Selecione um lote'}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3">
                {/* Seção Esquerda - ID e informações do usuário (remover nome e pontos) */}
                <div className="p-5 border-b md:border-b-0 border-gray-700/50">
                  <div className="flex flex-col">
                    <span className="text-white">ID: {id}</span>
                    {/* Nome e pontos removidos */}
                  </div>
                </div>
                
                {/* Seção Direita - Lances Ofertados */}
                <div className="p-5">
                  <h3 className="text-center text-white mb-4">Lances Ofertados</h3>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto pr-2">
                    {lances.map((lance, index) => (
                      <div key={lance.id} className="flex justify-between items-center">
                        <span className={index === 0 ? "text-white" : "text-white"}>{lance.empresa}</span>
                        <span className={index === 0 ? "text-white font-medium" : "text-white font-medium"}>
                          R$ {lance.valor.toLocaleString()},00
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Seção Inferior - Créditos de Carbono */}
              <div className="bg-[#0e7cb4] p-5 flex flex-col items-center">
                <h3 className="text-white mb-1">Créditos Carbono Disponíveis</h3>
                <p className="text-2xl font-bold text-white">{formatarNumeroGrande(creditosDisponiveis)} KgCO2e</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Gráfico */}
          <Card className="mb-6 bg-[#0e7cb4] text-white p-4">
            <CardHeader className="pt-3 pb-2 border-b border-blue-200">
              <div className="flex items-center justify-between">
                {/* Ícones removidos */}
                <CardTitle className="text-sm font-medium">Histórico de Lances</CardTitle>
                <Badge variant="outline" className="text-white border-white">Evolução</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-48 relative">
                
                <div className="absolute left-1 top-4 bottom-6 flex flex-col justify-between items-start">
                  <div className="text-[9px] text-white">14000</div>
                  <div className="text-[9px] text-white">12500</div>
                  <div className="text-[9px] text-white">10000</div>
                  <div className="text-[9px] text-white">7500</div>
                  <div className="text-[9px] text-white">5000</div>
                  <div className="text-[9px] text-white">2500</div>
                  <div className="text-[9px] text-white">1000</div>
                  <div className="text-[9px] text-white">0</div>
                </div>
                
                <div className="absolute inset-0 px-12 py-4">
                  <svg className="w-full h-full overflow-visible" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#fff" />
                        <stop offset="100%" stopColor="#fff" />
                      </linearGradient>
                    </defs>
                    <path 
                      d="M0,90 L60,70 L120,50 L180,30 L240,20 L300,10" 
                      fill="none" 
                      stroke="url(#lineGradient)" 
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Pontos do gráfico */}
                    <circle cx="0" cy="90" r="3" fill="#fff" />
                    <circle cx="0" cy="90" r="5" fill="#fff" fillOpacity="0.2" />
                    <circle cx="60" cy="70" r="3" fill="#fff" />
                    <circle cx="60" cy="70" r="5" fill="#fff" fillOpacity="0.2" />
                    <circle cx="120" cy="50" r="3" fill="#fff" />
                    <circle cx="120" cy="50" r="5" fill="#fff" fillOpacity="0.2" />
                    <circle cx="180" cy="30" r="3" fill="#fff" />
                    <circle cx="180" cy="30" r="5" fill="#fff" fillOpacity="0.2" />
                    <circle cx="240" cy="20" r="3" fill="#fff" />
                    <circle cx="240" cy="20" r="5" fill="#fff" fillOpacity="0.2" />
                    <circle cx="300" cy="10" r="3" fill="#fff" />
                    <circle cx="300" cy="10" r="5" fill="#fff" fillOpacity="0.2" />
                  </svg>
                </div>
                
                <div className="absolute left-12 right-12 bottom-0.5 flex justify-between items-center">
                  <div className="text-[9px] text-white">Stickler Digital</div>
                  <div className="text-[9px] text-white">Idbod</div>
                  <div className="text-[9px] text-white">Stickler Digital</div>
                  <div className="text-[9px] text-white">Idbod</div>
                  <div className="text-[9px] text-white">Stickler Digital</div>
                  <div className="text-[9px] text-white">Idbod</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Lista de 10 lotes de 500 KgCO2e cada */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-800">Lotes Recentes</h2>
              <ul className="space-y-2">
                {[...Array(10)].map((_, i) => (
                  <li key={i} className="flex items-center justify-between text-gray-700">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="lote"
                        className="mr-2 w-4 h-4 accent-[#0e7cb4]"
                        checked={loteSelecionado === 10 - i}
                        onChange={() => setLoteSelecionado(10 - i)}
                      />
                      <span>Lote Nº LT-2025-0{10 - i}</span>
                    </div>
                    <span className="font-bold text-[#0e7cb4]">500 KgCO2e</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          
          {/* Informações do Pregão */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-4">
                  <div className="bg-[#0e7cb4] text-white p-3 rounded-lg">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Pregão Nº</h3>
                    <p className="text-2xl font-bold text-[#0e7cb4]">PG-2025-002</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-green-600 text-white p-3 rounded-lg">
                    <Cloud className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Total de Créditos Disponíveis</h3>
                    <p className="text-2xl font-bold text-green-600">{formatarNumeroGrande(creditosDisponiveis)} KgCO2e</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Formulário de Lance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Enviar Lance</h2>
                
                {loteSelecionado && (
                  <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-sm font-medium">
                    Dando lance para: <span className="font-bold">Lote Nº LT-2025-0{loteSelecionado}</span>
                  </div>
                )}

                {erro && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{erro}</p>
                  </div>
                )}
                
                <form onSubmit={enviarLance}>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="valorLance" className="text-base">
                        Valor do Lance
                      </Label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <DollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          id="valorLance"
                          type="number"
                          value={valorLance}
                          onChange={(e) => setValorLance(e.target.value)}
                          className="pl-10"
                          placeholder="Digite o valor do seu lance"
                          min="1"
                          step="0.01"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        O lance mínimo deve ser maior que o último lance registrado.
                      </p>
                    </div>
                    
                    <div className="pt-4">
                      <Button 
                        type="submit"
                        className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar Lance'}
                      </Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Regras do Pregão</h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h3 className="flex items-center text-amber-800 font-medium mb-2">
                      <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
                      Atenção
                    </h3>
                    <p className="text-sm text-amber-700">
                      O lance é irrevogável. Uma vez enviado, não poderá ser cancelado ou alterado.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex">
                      <div className="mr-3 flex-shrink-0">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0e7cb4] text-white">
                          1
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">Os lances são apresentados em ordem decrescente</p>
                        <p className="text-sm text-gray-600">
                          O maior lance será destacado no topo da lista.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-3 flex-shrink-0">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0e7cb4] text-white">
                          2
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">Valor mínimo para incremento</p>
                        <p className="text-sm text-gray-600">
                          Cada lance deve superar o anterior em pelo menos R$ 500,00.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-3 flex-shrink-0">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0e7cb4] text-white">
                          3
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">Encerramento do pregão</p>
                        <p className="text-sm text-gray-600">
                          O pregão será encerrado quando o tempo terminar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PregaoLances; 